import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, Col, Container, Row } from 'react-bootstrap';
import { CardBody } from 'reactstrap';
import DataTable from 'react-data-table-component';
import { Btn } from '../../AbstractElements';
import {
  useGetExternalOrdersQuery,
  useUpdateOrderStatusMutation,
} from '../../features/externalOrdersApiSlice';
import {
  downloadCsv,
  downloadMultipleOrderPdfs,
  extractTrackingId,
  generateExternalOrderPdfBuffer,
  generateExternalLabelPdfBuffer,
} from '../../utils';
import { useReactToPrint } from 'react-to-print';
import ConfirmationModal from '../UiKits/ConfirmationModal';
import { toast } from 'react-toastify';
import { producedDays, monthsFromOrderMonths } from '../../utils/labelDays';
import { useNavigate } from 'react-router';
import {
  useGetIngredientPricesQuery,
  useGetIngredientsCodesQuery,
  useSendExternalOrderEmailUpdatesMutation,
  useAssignBatchMutation,
} from '../../features/ordersApiSlice';

const OMEGA3_B2B_MONTHLY_PRICE_EUR = 8.2;

const getOmega3Months = (value) => {
  const months = Number(value);
  return Number.isInteger(months) && months > 0 ? months : 0;
};

const hasFormulaProduct = (order) => Array.isArray(order?.ingredients) && order.ingredients.length > 0;

const hasOmega3Product = (order) => getOmega3Months(order?.omega3) > 0;

const getProductLabel = (order) => {
  const hasFormula = hasFormulaProduct(order);
  const hasOmega3 = hasOmega3Product(order);

  if (hasFormula && hasOmega3) return 'Formula + Omega-3';
  if (hasOmega3) return 'Omega-3';
  return 'Formula';
};

// SHI-528 — an Omega-3-only order has no dosed pouches: no pouch count to show, and no
// ingredient label to print. Defined off getProductLabel rather than re-deriving it, so the
// rule can never drift from what the Products column actually says.
const isOmega3Only = (order) => getProductLabel(order) === 'Omega-3';

const getOmega3Price = (order) => {
  if (!hasOmega3Product(order)) return 0;

  const storedPrice = parseFloat(order?.omega3_price_eur);
  if (!Number.isNaN(storedPrice)) return storedPrice;

  const months = getOmega3Months(order?.omega3);
  return Number((months * OMEGA3_B2B_MONTHLY_PRICE_EUR).toFixed(2));
};

const ExternalOrders = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [status, setStatus] = useState('pending');
  const [details, setDetails] = useState('pending');
  const [externalOrder, setExternalOrder] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [showError, setShowError] = useState({ status: '', details: '' });
  const [selectedRows, setSelectedRows] = useState();
  console.log('selectedRows => ', selectedRows);
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
  const [isDownloadingSummary, setIsDownloadingSummary] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [isDownloadingLabel, setIsDownloadingLabel] = useState(false);
  console.log('externalOrder => ', externalOrder);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [environmentFilter, setEnvironmentFilter] = useState('prod');

  const shippingAddressRef = useRef();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const {
    data: externalOrders,
    isLoading,
    isSuccess,
    refetch,
  } = useGetExternalOrdersQuery({
    page: currentPage,
    limit: pageSize,
    search: debouncedSearchTerm,
    status: statusFilter,
    environment: environmentFilter,
  });
  const { data: ingredientCodes } = useGetIngredientsCodesQuery();
  const { data: ingredientPrices } = useGetIngredientPricesQuery('en');
  const [updateOrderStatus] = useUpdateOrderStatusMutation();
  const [sendEmailUpdates, { isLoading: sendEmailUpdatesLoading }] =
    useSendExternalOrderEmailUpdatesMutation();
  const [assignBatch] = useAssignBatchMutation();

  console.log('DATA CUSTOMERS', externalOrders);

  useEffect(() => {
    refetch();
  }, [refetch, currentPage, pageSize, debouncedSearchTerm, statusFilter, environmentFilter]);

  useEffect(() => {
    const fn = () => {
      if (isSuccess && externalOrders?.orders) {
        setData(externalOrders?.orders || []);
        setTotalRows(externalOrders?.pagination?.total || 0);
      }
    };
    fn();
  }, [externalOrders]);

  const handleAction = async (row) => {
    setExternalOrder(row);
  };

  const printShipAddress = useReactToPrint({
    content: () => shippingAddressRef.current,
  });

  const toggleModal = () => {
    showErrorMessage('status', '');
    showErrorMessage('details', '');

    if (status === '') {
      showErrorMessage('status', 'Please select status.');
    } else {
      if (externalOrder?.id) {
        setModalOpen(!modalOpen);
      }
    }
  };

  const showErrorMessage = (object, message) => {
    setShowError((prevValues) => ({
      ...prevValues,
      [object]: message,
    }));
  };

  const handleConfirmAction = () => {
    handleUpdateStatus();
  };

  const handleUpdateStatus = async () => {
    try {
      const res = await updateOrderStatus({
        orderId: externalOrder?.id,
        status: status,
        details: details,
      }).unwrap();
      console.log(res);
      toast.success(res?.message || 'Order status updated successfully');
    } catch (e) {
      console.log(e);
    }
  };

  const cogs = useMemo(
    () =>
      externalOrder?.ingredients?.reduce((sum, item) => {
        return sum + (parseFloat(item.cogs) || 0);
      }, 0) || 0,
    [externalOrder],
  );
  const numberOfMonths = parseInt(externalOrder?.order_months) || 1;
  const totalOrderCogs = cogs * 30 * numberOfMonths;
  const externalOrderOmega3Price = getOmega3Price(externalOrder);
  const externalOrderFormulaPrice = Math.max(
    (parseFloat(externalOrder?.b2b_product_price_eur) || 0) -
      externalOrderOmega3Price -
      (parseFloat(externalOrder?.shipping_price) || 0),
    0,
  );

  const tableColumns = [
    {
      name: 'Id',
      selector: (row) => `${row?.id}`,
      width: '70px',
    },
    {
      name: 'Internal Order Id',
      selector: (row) => `${row?.internal_order_id ? row.internal_order_id : ''}`,
    },
    {
      name: 'Order Id',
      selector: (row) => `${row?.order_id}`,
    },
    {
      name: 'Name',
      selector: (row) => row?.user?.full_name,
      sortable: true,
      center: false,
    },
    {
      name: 'Email',
      selector: (row) => `${row?.user?.email}`,
      sortable: true,
      center: false,
    },
    {
      name: 'Phone Number',
      selector: (row) => `${row?.user?.phone_number}`,
      center: false,
    },
    {
      name: 'Preparation Date',
      selector: (row) => new Date(row?.preparation_date),
      sortable: true,
      center: false,
      cell: (row) => new Date(row?.preparation_date).toDateString(),
    },
    {
      name: 'Source Company Name',
      selector: (row) => `${row.user?.source_company_name}`,
      center: false,
      sortable: true,
    },
    {
      name: 'Status',
      selector: (row) => `${row?.status}`,
      center: false,
      sortable: true,
    },
    {
      name: 'Products',
      selector: (row) => getProductLabel(row),
      center: false,
      sortable: true,
      width: '180px',
      cell: (row) => (
        <span className="t-inline-flex t-items-center t-rounded-full t-bg-blue-50 t-px-3 t-py-1 t-text-xs t-font-medium t-text-blue-800">
          {getProductLabel(row)}
        </span>
      ),
    },
    {
      name: 'Order Months',
      selector: (row) => `${row.order_months}`,
      // SHI-528 — the real pouch count under the duration, so whoever prints labels sees
      // what the bag actually holds. External orders carry a literal month count and have
      // no dosing-days override, so this is always months x 31.
      cell: (row) => (
        <div>
          <div>{row.order_months}</div>
          {/* An Omega-3-only order has no dosed pouches, so there is no count to show. */}
          {!isOmega3Only(row) && (
            <div style={{ fontSize: '11px', lineHeight: 1.2, color: '#6b7280' }}>
              {producedDays(monthsFromOrderMonths(row.order_months), null)} pouches
            </div>
          )}
        </div>
      ),
      center: false,
      sortable: true,
    },
    {
      name: 'Email Count',
      selector: (row) => row['email_count'],
      sortable: true,
      center: false,
    },
    {
      name: 'Action',
      width: '140px',
      cell: (row) => (
        <Row className="flex flex-row">
          <Btn attrBtn={{ color: 'primary', onClick: () => handleAction(row) }} className="col-6">
            {'View'}
          </Btn>
          {/* <Btn
            attrBtn={{
              color: 'primary',
              onClick: () =>
                navigate(`/admin/external-orders-pdf`, {
                  state: {
                    order: row,
                  },
                }),
            }}
            className="col-6"
          >
            Details
          </Btn> */}
        </Row>
      ),
    },
  ];

  const handleRowSelected = useCallback((state) => {
    setSelectedRows(state.selectedRows);
  }, []);

  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
  };

  const handleStatusFilter = (event) => {
    const value = event.target.value;
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleEnvironmentFilter = (event) => {
    setEnvironmentFilter(event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPageSize(newPerPage);
    setCurrentPage(page);
  };

  const downloadAllSelectedOrders = async () => {
    if (!selectedRows?.length) {
      toast.error('Please select at least one order to download');
      return;
    }

    if (!ingredientPrices) {
      toast.error('Required data is still loading. Please wait and try again.');
      return;
    }

    const loadingToast = toast.loading('Generating PDFs...');
    try {
      setIsDownloadingPdf(true);
      // Show loading toast
      const allOrdersData = selectedRows;

      // Generate PDF for each order
      const generateOrderPdf = async (order) => {
        // Create deep copy of the order data
        const mergedOrder = JSON.parse(JSON.stringify(order));
        return await generateExternalOrderPdfBuffer(mergedOrder, ingredientPrices, ingredientCodes);
      };

      // Download all PDFs as a single file
      await downloadMultipleOrderPdfs(allOrdersData, generateOrderPdf);

      // Close loading toast and show success
      toast.dismiss(loadingToast);
      toast.success(`Successfully downloaded ${selectedRows.length} order PDF(s)`);
    } catch (error) {
      console.error('Error downloading orders:', error);
      toast.error('Error generating PDFs. Please try again.');
    } finally {
      toast.dismiss(loadingToast);
      setIsDownloadingPdf(false);
    }
  };

  const downloadAllSelectedLabels = async () => {
    if (!selectedRows?.length) {
      toast.error('Please select at least one order to download labels');
      return;
    }

    if (!ingredientPrices) {
      toast.error('Required data is still loading. Please wait and try again.');
      return;
    }

    // Prompt for batch number
    const input = window.prompt('Enter batch number (e.g. 123):');
    if (!input || !input.trim()) return;
    const batch = 'PROD' + input.trim();

    // Assign batch on the server
    try {
      await assignBatch({
        orderIds: selectedRows.map((row) => row.id),
        batch,
        type: 'external',
      }).unwrap();
    } catch (error) {
      console.error('Error assigning batch:', error);
      toast.error('Error assigning batch. Please try again.');
      return;
    }

    const loadingToast = toast.loading('Generating Labels...');
    try {
      setIsDownloadingLabel(true);
      // SHI-528 — Omega-3-only orders have no dosed pouches and so no ingredient label.
      // Skipping them keeps blank 0g pages out of the stack.
      const allOrdersData = selectedRows.filter((o) => !isOmega3Only(o));
      const skipped = selectedRows.length - allOrdersData.length;

      if (!allOrdersData.length) {
        toast.dismiss(loadingToast);
        toast.error('Nothing to print — every selected order is Omega-3 only, which has no ingredient label.');
        setIsDownloadingLabel(false);
        return;
      }

      const generateLabelPdf = async (order) => {
        const mergedOrder = JSON.parse(JSON.stringify(order));
        return await generateExternalLabelPdfBuffer(mergedOrder, ingredientPrices, ingredientCodes);
      };

      await downloadMultipleOrderPdfs(
        allOrdersData,
        generateLabelPdf,
        `Ingredients Labels external orders - ${batch}.pdf`,
      );

      toast.dismiss(loadingToast);
      toast.success(
        `Successfully downloaded ${allOrdersData.length} label PDF(s)`
        + (skipped ? ` — skipped ${skipped} Omega-3-only order${skipped === 1 ? '' : 's'} (no pouches)` : ''),
      );
    } catch (error) {
      console.error('Error downloading labels:', error);
      toast.error('Error generating label PDFs. Please try again.');
    } finally {
      toast.dismiss(loadingToast);
      setIsDownloadingLabel(false);
    }
  };

  const downloadWeightCalculationsCSV = async () => {
    if (!selectedRows?.length) {
      toast.error('Please select at least one order to download CSV');
      return;
    }

    try {
      setIsDownloadingCsv(true);
      const loadingToast = toast.loading('Generating CSV...');
      const allOrdersData = selectedRows;

      const allIngredients = new Set();
      allOrdersData.forEach((order) => {
        order.ingredients?.forEach((ingredient) => {
          allIngredients.add(ingredient.ingredientName);
        });
      });

      const csvData = [];
      const headers = ['Ingredient'];

      allOrdersData.forEach((order) => {
        const orderId = order?.internal_order_id;
        if (orderId) {
          headers.push(`Order ${orderId}`);
        }
      });

      Array.from(allIngredients).forEach((ingredientName) => {
        const row = { Ingredient: ingredientName };

        allOrdersData.forEach((order) => {
          const orderId = order?.internal_order_id;
          const duration = order?.order_months;
          const days = parseInt(duration) * 31;
          const ingredient = order.ingredients?.find((ing) => ing.ingredientName === ingredientName);

          if (ingredient) {
            let quantity = ingredient.quantity;
            // if (editedIngredients[orderId]?.[ingredient.id]) {
            //   quantity = editedIngredients[orderId][ingredient.id];
            // }
            const price = ingredientPrices?.data?.find(
              (item) => item?.ingredient_id === ingredient?.ingredientId,
            );
            const activePercentage = price?.active_percentage || 100;
            console.log('activePercentage => ', price?.name, quantity, activePercentage, days);
            const weight = (parseFloat(quantity) * days) / activePercentage;
            row[`Order ${orderId}`] = Math.round(weight);
          } else {
            row[`Order ${orderId}`] = '0.00';
          }
        });

        csvData.push(row);
      });
      downloadCsv(csvData, headers);
      toast.dismiss(loadingToast);
      toast.success(`Successfully downloaded weight calculations CSV`);
    } catch (error) {
      console.error('Error generating CSV:', error);
      toast.error('Error generating CSV. Please try again.');
    } finally {
      setIsDownloadingCsv(false);
    }
  };

  const downloadSummaryOrdersCSV = async () => {
    if (!selectedRows?.length) {
      toast.error('Please select at least one order to download summary CSV');
      return;
    }

    try {
      setIsDownloadingSummary(true);
      const loadingToast = toast.loading('Generating Summary CSV...');
      const allOrdersData = selectedRows;
      const csvData = [];

      allOrdersData.forEach((order, index) => {
        const orderId = order?.internal_order_id;
        const duration = order?.order_months;
        const isFirstOrder = order?.isFirstOrder === 1 ? 'Y' : 'N';
        const days = parseInt(duration) * 31;
        const monthsToPrepare = order?.order_months;
        const dailyPouches = parseInt(duration) * 31;
        const months = duration === '1' ? '1 month' : duration + ' months';

        // Get edited name if available
        // const tableRow = selectedRows.find((row) => row.internal_order_id === orderId);
        let customerName = `${order?.user?.full_name}`;
        // if (tableRow && editedData[tableRow.id]?.name) {
        //   customerName = editedData[tableRow.id].name;
        // }

        // Calculate total weight per daily pouch
        let totalWeight = 0;
        order.ingredients?.forEach((ingredient) => {
          let quantity = ingredient.quantity;
          // if (editedIngredients[orderId]?.[ingredient.id]) {
          //   quantity = editedIngredients[orderId][ingredient.id];
          // }
          const price = ingredientPrices?.data?.find(
            (item) => item?.ingredient_id === parseInt(ingredient?.ingredientId),
          );
          const activePercentage = price?.active_percentage || 100;
          const weight = (parseFloat(quantity) * days) / activePercentage;
          // console.log('weight => ', price?.name, quantity, activePercentage, days, weight);
          totalWeight += weight;
        });

        const weightPerDailyPouch = totalWeight / days;

        csvData.push({
          '#': index + 1,
          'Order id': orderId,
          Plan: duration,
          'Welcome pack (Y/N)': isFirstOrder,
          'Weight per daily pouch': Math.round(weightPerDailyPouch * 100) / 100,
          '#months to prepare': monthsToPrepare,
          '#daily pouches': dailyPouches,
          name: customerName,
          month: months,
        });
      });

      const headers = [
        '#',
        'Order id',
        'Plan',
        'Welcome pack (Y/N)',
        'Weight per daily pouch',
        '#months to prepare',
        '#daily pouches',
        'name',
        'month',
      ];

      downloadCsv(csvData, headers);
      toast.dismiss(loadingToast);
      toast.success(`Successfully downloaded summary orders CSV`);
    } catch (error) {
      console.error('Error generating summary CSV:', error);
      toast.error('Error generating summary CSV. Please try again.');
    } finally {
      setIsDownloadingSummary(false);
    }
  };

  const createShipment = async () => {
    // SHI-528 — see OrdersPdf: never build "?orderIds=undefined" for the next page.
    const orderIds = (selectedRows || []).map((item) => item?.id).filter((id) => id != null);
    if (!orderIds.length) {
      toast.error('Please select at least one order to create labels.');
      return;
    }
    const allSameCompany = selectedRows.every(
      (item) => item?.user?.source_company_name === selectedRows[0]?.user?.source_company_name,
    );
    if (!allSameCompany) {
      toast.error('Please select labels only for the same partner!');
      return;
    }
    navigate(`/admin/external-orders-pdf/shipment-labels?orderIds=${JSON.stringify(orderIds)}`);
  };
  console.log('selectedRows => ', selectedRows);

  const getShipmentTracking = async () => {
    if (!selectedRows?.length) {
      toast.error('Please select at least one order to download summary CSV');
      return;
    }
    const ordersWithTrackingId = selectedRows?.filter((item) => !!item?.order_tracking_link);
    if (ordersWithTrackingId?.length === 0) {
      toast.error('No order with tracking id');
      return;
    }
    console.log('ordersWithTrackingId => ', ordersWithTrackingId);
    const trackingIds = ordersWithTrackingId?.map((item) => extractTrackingId(item?.order_tracking_link));
    console.log('trackingIds => ', trackingIds);
    navigate(`/admin/external-orders-pdf/shipment-tracking?trackingIds=${JSON.stringify(trackingIds)}`, {
      state: {
        ordersWithTrackingId,
      },
    });
  };
  console.log(
    'data => ',
    data?.filter((item) => item?.order_tracking_link),
  );
  console.log('selectedRows => ', selectedRows);

  const sendEmailUpdatesSubmit = async () => {
    try {
      if (selectedRows?.length === 0) {
        toast.error('Please select orders to send emails!');
        return;
      }
      const loadingToast = toast.loading('Sending email updates...');
      const orderIds = selectedRows?.map((item) => item?.id);
      const res = await sendEmailUpdates({ orderIds });
      toast.dismiss(loadingToast);
      if (res?.error?.data) {
        toast.error(res?.error?.data?.message || 'Something went wrong!');
      } else {
        toast.success('Order updates sent successfully!');
      }
    } catch (err) {
      toast.error(err?.message || `Something went wrong`);
      console.log('Error => ', err);
    }
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <>
      <h1 className="t-text-xl t-font-bold t-mb-2 t-ml-2 md:t-ml-3 t-text-secondary">External Orders</h1>
      <Container fluid>
        <Card>
          <CardBody>
            <Row>
              <div className="col-8">
                <input
                  type="text"
                  placeholder="Search by order ID, name, email, or status"
                  value={searchTerm}
                  onChange={handleSearch}
                  className="form-control col-8"
                />
              </div>
              <div className="col-2">
                <select className="form-control" value={environmentFilter} onChange={handleEnvironmentFilter}>
                  <option value="">All</option>
                  <option value="prod">Prod</option>
                  <option value="dev">Dev</option>
                </select>
              </div>
              <div className="col-2">
                <select className="form-control" onChange={handleStatusFilter}>
                  <option value="" hidden disabled>
                    Select Status
                  </option>
                  <option value="">All</option>
                  <option value="pending">Pending</option>
                  <option value="dispatched">Dispatched</option>
                  <option value="delivered">Delivered</option>
                  <option value="failure">Failure</option>
                  <option value="pre-transit">Pre transit</option>
                  <option value="transit">Transit</option>
                  <option value="unknown">Unknown</option>
                </select>
              </div>
            </Row>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <div className="text-end t-flex t-flex-wrap t-gap-2 t-justify-end">
              <Btn
                attrBtn={{
                  className: 'mr-3',
                  color: 'primary',
                  disabled: !selectedRows?.length || !ingredientPrices || isDownloadingPdf,
                  onClick: downloadAllSelectedOrders,
                }}
              >
                {isDownloadingPdf ? 'Generating PDFs...' : 'Download'}
              </Btn>
              <Btn
                attrBtn={{
                  className: 'mr-3',
                  color: 'primary',
                  disabled: !selectedRows?.length || !ingredientPrices || isDownloadingLabel,
                  onClick: downloadAllSelectedLabels,
                }}
              >
                {isDownloadingLabel ? 'Generating Labels...' : 'Labels'}
              </Btn>
              <Btn
                attrBtn={{
                  className: 'mr-2',
                  color: 'secondary',
                  disabled: !selectedRows?.length || isDownloadingCsv,
                  onClick: downloadWeightCalculationsCSV,
                }}
              >
                {isDownloadingCsv ? 'Generating CSV...' : 'Download Weight CSV'}
              </Btn>
              <Btn
                attrBtn={{
                  className: 'mr-2',
                  color: 'info',
                  disabled: !selectedRows?.length || isDownloadingSummary,
                  onClick: downloadSummaryOrdersCSV,
                }}
              >
                {isDownloadingSummary ? 'Generating Summary...' : 'Download Summary Orders'}
              </Btn>
              <Btn
                attrBtn={{
                  className: 'mr-2',
                  color: 'info',
                  disabled: !selectedRows?.length,
                  onClick: createShipment,
                }}
              >
                Shipment
              </Btn>
              <Btn
                attrBtn={{
                  className: 'mr-2',
                  color: 'info',
                  disabled: !selectedRows?.length,
                  onClick: getShipmentTracking,
                }}
              >
                Get Tracking
              </Btn>
              <Btn
                attrBtn={{
                  className: 'mr-2',
                  color: 'info',
                  disabled: !selectedRows?.length || sendEmailUpdatesLoading,
                  onClick: sendEmailUpdatesSubmit,
                }}
              >
                Send Email Updates
              </Btn>
            </div>

            {isLoading && (
              <div className="text-center py-3 mt-3">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading orders...</p>
              </div>
            )}

            {!isLoading && (
              <div className="mb-3 text-muted mt-3">
                Showing {data.length} of {totalRows} orders
                {searchTerm && ` (filtered by "${searchTerm}")`}
                {statusFilter && ` (status: ${statusFilter})`}
              </div>
            )}

            <DataTable
              columns={tableColumns}
              data={data}
              striped={true}
              center={true}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePerRowsChange}
              progressPending={isLoading}
              selectableRowsHighlight
              paginationPerPage={pageSize}
              paginationRowsPerPageOptions={[10, 25, 50, 100]}
              paginationComponentOptions={{
                noRowsPerPage: false, // Hide the dropdown that allows changing the number of rows per page
              }}
              selectableRows
              onSelectedRowsChange={handleRowSelected}
              clearSelectedRows={false}
            />
          </CardBody>
        </Card>
        {externalOrder?.order_id ? (
          <>
            <Row>
              <Col sm="7">
                <Card>
                  <CardBody>
                    <div className="row">
                      <div className="col-sm-6">
                        <div className="text-start">
                          <h3>
                            Order # <span className="digits counter">{externalOrder?.order_id}</span>
                          </h3>
                          <p>
                            Preparation Date: {new Date(externalOrder?.preparation_date).toDateString()}
                            <br />
                            Current Status:{' '}
                            <span style={{ textTransform: 'capitalize' }}>{externalOrder?.status}</span>
                          </p>
                        </div>
                      </div>
                      <div className="col-sm-6 text-end">
                        <div ref={shippingAddressRef} className="m-2 mt-0">
                          <h3>Shipping Address</h3>
                          <p>
                            {externalOrder?.user?.source_company_name}
                            <br />
                            {externalOrder?.user?.full_name}
                            <br />
                            {externalOrder?.user?.address}
                            <br />
                            {externalOrder?.user?.city}&nbsp;(
                            {externalOrder?.user?.zip_code})
                            <br />
                            {externalOrder?.user?.country}
                            <br />
                            {externalOrder?.user?.phone_number}
                          </p>
                        </div>
                        <Btn
                          attrBtn={{
                            className: 'mt-3',
                            color: 'primary',
                            onClick: () => printShipAddress(),
                          }}
                        >
                          <i class="fa fa-print me-1" /> {'Print'}
                        </Btn>
                      </div>
                    </div>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <h4> Total Material Cogs</h4>
                    <p>{totalOrderCogs.toFixed(2)}</p>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <h4>Products</h4>
                    <table className="table">
                      <thead>
                        <tr>
                          <th scope="col">Product</th>
                          <th scope="col">Duration</th>
                          <th scope="col">Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {hasFormulaProduct(externalOrder) && (
                          <tr>
                            <td>Riise</td>
                            <td>
                              {externalOrder?.order_months} month
                              {externalOrder?.order_months === '1' ? '' : 's'}
                            </td>
                            <td>€{externalOrderFormulaPrice.toFixed(2)}</td>
                          </tr>
                        )}
                        {hasOmega3Product(externalOrder) && (
                          <tr>
                            <td>Omega-3</td>
                            <td>
                              {externalOrder?.omega3} month{externalOrder?.omega3 === '1' ? '' : 's'}
                            </td>
                            <td>€{externalOrderOmega3Price.toFixed(2)}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <Row>
                      <div className="col-sm-6">
                        <h4>Add Status</h4>
                        <select className="form-control" onChange={(e) => setStatus(e.target.value)}>
                          <option value="">--Select Status--</option>
                          <option value="Under Process">Under Process</option>
                          <option value="Ready to dispatch">Ready to dispatch</option>
                          <option value="Dispatched">Dispatched</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                        {showError.status === '' ? (
                          ''
                        ) : (
                          <span className="text-danger">Please select status</span>
                        )}
                        <input
                          type="text"
                          onChange={(e) => {
                            e.preventDefault();
                            setDetails(e.target.value);
                          }}
                          className="form-control mt-3"
                          placeholder="Tracking number"
                        />

                        <Btn
                          attrBtn={{
                            className: 'mt-3',
                            color: 'primary',
                            onClick: () => toggleModal(),
                          }}
                        >
                          {'Update Status'}
                        </Btn>
                      </div>
                    </Row>
                  </CardBody>
                </Card>
              </Col>

              <Col sm="5">
                <Card>
                  <CardBody className="personalized-formula">
                    <div className="table-responsive">
                      <div className="text-end">
                        <Btn
                          attrBtn={{
                            className: 'mr-2',
                            color: 'primary',
                            disabled: !externalOrder?.ingredients?.length,
                            onClick: () =>
                              downloadCsv(externalOrder?.ingredients, [
                                'ingredientId',
                                'ingredientName',
                                'quantity',
                              ]),
                          }}
                        >
                          Download
                        </Btn>
                      </div>
                      <table className="table accordion" id="accordionExample">
                        <thead>
                          <tr>
                            <th scope="col" className="col-8">
                              Ingredient Id
                            </th>
                            <th scope="col">Name</th>
                            <th scope="col">Quantity</th>
                            <th scope="col">Daily Cogs</th>
                          </tr>
                        </thead>
                        <tbody>
                          {externalOrder?.ingredients &&
                            externalOrder?.ingredients.map((item) => (
                              <tr key={'ingredient-' + item?.ingredientId}>
                                <td>{item.ingredientId}</td>
                                <td>{item.ingredientName}</td>
                                <td>{item.quantity}</td>
                                <td>{item?.cogs ? item.cogs : ''}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </>
        ) : null}
        <ConfirmationModal
          message={'Are you sure to update the status?'}
          isOpen={modalOpen}
          toggle={toggleModal}
          onConfirm={handleConfirmAction}
        />
      </Container>
    </>
  );
};

export default ExternalOrders;
